// Register.jsx — Création d'asso et compte (redesign éditorial)
// Garde toute la logique : Vous → Asso → Plan
import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/lib/auth';
import { toast } from 'sonner';
import { ArrowRight, Loader2, Check } from 'lucide-react';
import { Logo } from '@/components/Logo';

const TYPES = [
  { value: 'sport', label: 'Club sportif' },
  { value: 'reseau', label: 'Réseau professionnel' },
  { value: 'culture', label: 'Culturel' },
  { value: 'caritatif', label: 'Caritatif' },
  { value: 'education', label: 'Éducation' },
  { value: 'autre', label: 'Autre' },
];

const COLORS = ['#0047FF', '#10B981', '#8B5CF6', '#EC4899', '#F59E0B', '#EF4444', '#06B6D4', '#0F172A'];

const PLANS = [
  { id: 'starter', name: 'Starter', price: 20, desc: 'Jusqu\'à 100 membres' },
  { id: 'pro', name: 'Pro', price: 45, desc: 'Jusqu\'à 300 membres', popular: true },
  { id: 'network', name: 'Network', price: 90, desc: 'Membres illimités' },
];

export default function Register() {
  const { register, loading } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [form, setForm] = useState({
    prenom: '',
    nom: '',
    email: '',
    mot_de_passe: '',
    asso_nom: '',
    asso_type: 'sport',
    asso_ville: '',
    asso_couleur: '#0047FF',
    plan_id: params.get('plan') || 'starter',
  });
  const [err, setErr] = useState(null);

  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handle = async (e) => {
    e.preventDefault();
    setErr(null);
    try {
      await register(form);
      toast.success('Compte créé ! Bienvenue.');
      navigate('/dashboard');
    } catch (e) {
      const msg = e?.response?.data?.error || 'Erreur lors de la création';
      setErr(msg);
      toast.error(msg);
    }
  };

  return (
    <div className="min-h-screen bg-[#F9F9F8] text-[#0F172A] font-sans antialiased" data-testid="register-page">

      {/* Header minimal */}
      <header className="sticky top-0 z-30 bg-white/85 backdrop-blur-xl border-b border-[#E2E8F0]">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5 group">
            <Logo size={30} className="transition-transform group-hover:scale-105" />
            <span className="font-display font-black tracking-tight text-[#0F172A]">AssoPerf</span>
          </Link>
          <Link to="/login" className="text-sm font-semibold text-[#475569] hover:text-[#0F172A] transition-colors" data-testid="register-login-link">
            Déjà un compte ? <span className="text-[#0F172A] underline underline-offset-4">Se connecter</span>
          </Link>
        </div>
      </header>

      <div className="max-w-3xl mx-auto px-6 lg:px-8 py-12 sm:py-16">
        {/* Intro éditoriale */}
        <div className="mb-12 max-w-2xl">
          <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#0047FF] mb-4">Créer mon espace</div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight text-[#0F172A] leading-[1.02] mb-4">
            Lancez votre espace <span className="italic font-medium text-[#0047FF]">en 60 secondes.</span>
          </h1>
          <p className="text-base sm:text-lg text-[#475569] leading-relaxed">
            Aucune carte bancaire requise · 14 jours d'essai gratuits · Annulation en 1 clic.
          </p>
        </div>

        <form onSubmit={handle} className="space-y-6" data-testid="register-form">

          {/* Section 1 — Vous */}
          <section className="bg-white border border-[#E2E8F0] rounded-3xl p-7 sm:p-8">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-7 h-7 rounded-full bg-[#0047FF] text-white grid place-items-center font-display font-black text-xs">1</div>
              <h2 className="font-display text-xl font-bold text-[#0F172A]">Vous</h2>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field label="Prénom">
                <input type="text" required value={form.prenom} onChange={(e) => upd('prenom', e.target.value)} className="ap-input" data-testid="register-prenom-input" />
              </Field>
              <Field label="Nom">
                <input type="text" required value={form.nom} onChange={(e) => upd('nom', e.target.value)} className="ap-input" data-testid="register-nom-input" />
              </Field>
              <Field label="Email">
                <input type="email" required value={form.email} onChange={(e) => upd('email', e.target.value)} className="ap-input" placeholder="vous@exemple.fr" data-testid="register-email-input" />
              </Field>
              <Field label="Mot de passe (6+ caractères)">
                <input type="password" required minLength={6} value={form.mot_de_passe} onChange={(e) => upd('mot_de_passe', e.target.value)} className="ap-input" placeholder="••••••••" data-testid="register-password-input" />
              </Field>
            </div>
          </section>

          {/* Section 2 — Association */}
          <section className="bg-white border border-[#E2E8F0] rounded-3xl p-7 sm:p-8">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-7 h-7 rounded-full bg-[#0047FF] text-white grid place-items-center font-display font-black text-xs">2</div>
              <h2 className="font-display text-xl font-bold text-[#0F172A]">Votre association</h2>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="sm:col-span-2">
                <Field label="Nom de l'association (telle que déclarée)">
                  <input type="text" required value={form.asso_nom} onChange={(e) => upd('asso_nom', e.target.value)} className="ap-input" placeholder="Ex: Club Rugby Figeac, REF Lyon…" data-testid="register-asso-nom-input" />
                </Field>
              </div>
              <Field label="Type">
                <select value={form.asso_type} onChange={(e) => upd('asso_type', e.target.value)} className="ap-input" data-testid="register-asso-type-select">
                  {TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                </select>
              </Field>
              <Field label="Ville">
                <input type="text" value={form.asso_ville} onChange={(e) => upd('asso_ville', e.target.value)} className="ap-input" placeholder="Paris, Lyon…" data-testid="register-ville-input" />
              </Field>
              <div className="sm:col-span-2">
                <label className="block text-[10px] font-bold uppercase tracking-[0.14em] text-[#475569] mb-2.5">Couleur principale</label>
                <div className="flex gap-2 flex-wrap" data-testid="register-color-picker">
                  {COLORS.map((c) => (
                    <button
                      key={c}
                      type="button"
                      onClick={() => upd('asso_couleur', c)}
                      className={`w-10 h-10 rounded-xl transition-all ${form.asso_couleur === c ? 'ring-2 ring-offset-2 ring-[#0F172A] scale-110' : 'hover:scale-105'}`}
                      style={{ background: c }}
                      data-testid={`register-color-${c.slice(1)}`}
                      aria-label={c}
                    />
                  ))}
                </div>
              </div>
            </div>
          </section>

          {/* Section 3 — Plan */}
          <section className="bg-white border border-[#E2E8F0] rounded-3xl p-7 sm:p-8">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-7 h-7 rounded-full bg-[#0047FF] text-white grid place-items-center font-display font-black text-xs">3</div>
              <h2 className="font-display text-xl font-bold text-[#0F172A]">Votre plan</h2>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              {PLANS.map((p) => {
                const selected = form.plan_id === p.id;
                return (
                  <button
                    key={p.id}
                    type="button"
                    onClick={() => upd('plan_id', p.id)}
                    className={`relative text-left p-5 rounded-2xl border-2 transition-all ${selected ? 'border-[#0047FF] bg-[#0047FF]/5 shadow-sm' : 'border-[#E2E8F0] hover:border-[#94A3B8]'}`}
                    data-testid={`register-plan-${p.id}`}
                  >
                    {p.popular && (
                      <span className="absolute -top-2.5 left-4 px-2 py-0.5 rounded-full bg-[#0047FF] text-white text-[9px] font-black uppercase tracking-[0.16em]">Populaire</span>
                    )}
                    {selected && (
                      <span className="absolute top-3 right-3 w-5 h-5 rounded-full bg-[#0047FF] text-white grid place-items-center">
                        <Check className="w-3 h-3" />
                      </span>
                    )}
                    <div className="font-display font-bold text-[#0F172A] text-base">{p.name}</div>
                    <div className="mt-1.5">
                      <span className="font-display text-3xl font-black text-[#0F172A] tracking-tight">{p.price}</span>
                      <span className="text-xs text-[#475569] ml-1">€/mois</span>
                    </div>
                    <div className="text-xs text-[#475569] mt-2 leading-snug">{p.desc}</div>
                  </button>
                );
              })}
            </div>
          </section>

          {err && (
            <div className="text-sm text-[#B91C1C] bg-[#FEF2F2] border border-[#FECACA] rounded-xl px-4 py-3" data-testid="register-error">
              {err}
            </div>
          )}

          <button
            type="submit"
            disabled={loading}
            className="group w-full inline-flex items-center justify-center gap-2 px-6 py-4 rounded-2xl bg-[#0047FF] hover:bg-[#0040E0] text-white font-bold text-base transition-all shadow-md hover:shadow-lg hover:-translate-y-0.5 disabled:opacity-60 disabled:hover:translate-y-0"
            data-testid="register-submit-button"
          >
            {loading ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <>
                Créer mon espace
                <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5" />
              </>
            )}
          </button>

          <p className="text-center text-xs text-[#94A3B8] pt-2">
            En créant votre compte, vous acceptez nos{' '}
            <Link to="/cgu" className="underline hover:text-[#0F172A]">CGU</Link> et notre{' '}
            <Link to="/confidentialite" className="underline hover:text-[#0F172A]">politique de confidentialité</Link>.
          </p>
        </form>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="block text-[10px] font-bold uppercase tracking-[0.14em] text-[#475569] mb-2">{label}</label>
      {children}
    </div>
  );
}
